import SuggestionsTermList from 'site/components/search/SuggestionsTermList.tsx'
import SuggestionsProductList, {
    mapProductToSearchSuggestionProduct,
    type SearchSuggestionProduct,
} from 'site/components/search/SuggestionsProductList.tsx'
import { useAutocomplete } from 'site/sdk/useAutocomplete.ts'
import { Suggestions } from 'site/components/search/SearchBar.tsx'
import { searchValue } from 'site/components/search/SearchBarJS.tsx'
import { levenshteinDistanceFlexible } from '../../utils/levenshteinDistance.ts'
import AlgoliaHitsViewed from 'site/islands/AlgoliaHitsViewed.tsx'
import AlgoliaUserToken from 'site/islands/AlgoliaUserToken.tsx'
import {
    buildAlgoliaInsightsFromProducts,
    getAlgoliaHitInsight,
} from 'site/utils/algoliaInsights.ts'

interface Props {
    query?: string
    /** @title Sugestões de produtos da barra de pesquisa */
    products?: SearchSuggestionProduct[] | null

    /** @title Sugestões da barra de pesquisa */
    searchSuggestions?: Suggestions[]
    /**
     * @hide true
     */
    isMobile?: boolean
    algolia?: boolean
}

export default function SearchBarList({ query, products, searchSuggestions, isMobile, algolia }: Props) {
    const { suggestions, loading } = useAutocomplete()

    const suggestionProducts = suggestions.value?.products ?? []
    const algoliaInsights = algolia
        ? buildAlgoliaInsightsFromProducts(suggestionProducts)
        : null

    const mappedSuggestionProducts: SearchSuggestionProduct[] = suggestionProducts.map(
        (product, index) => ({
            ...mapProductToSearchSuggestionProduct(product),
            algoliaHit: getAlgoliaHitInsight(algoliaInsights, index),
        }),
    )

    const filteredSearches = suggestions?.value?.searches?.filter((item) => {
        const distance = levenshteinDistanceFlexible(
            item.term.toLowerCase(),
            searchValue.value.toLowerCase(),
        )

        return distance < 6
    })
    const emptySuggestions = filteredSearches?.length === 0 || suggestions.value === null

    if (loading.value) return null

    return (
        <div
            class={`absolute top-5 left-0 z-10 bg-white w-full transition-all shadow-lg hidden
group-focus-within:block rounded-b-2xl lg:rounded-none border border-neutral-4 lg:border-none`}
        >
            {algolia && <AlgoliaUserToken />}
            <div class='flex flex-col lg:flex-row py-2 gap-2 w-full mt-4 lg:mt-10 pr-4 lg:pr-[48px] pl-4 lg:pl-[56px]'>
                {suggestions.value && !emptySuggestions && (
                    <>
                        <SuggestionsTermList
                            terms={filteredSearches}
                            inputValue={searchValue.value}
                        />
                        <div className='h-[315px] mr-[56px] border-l border-neutral-4 hidden lg:block' />
                        <div>
                            <SuggestionsProductList
                                products={mappedSuggestionProducts}
                                searchValue={searchValue}
                                query={query ?? ''}
                                algoliaIndex={algoliaInsights?.indexName}
                            />
                            {algolia && algoliaInsights && (
                                <AlgoliaHitsViewed queryId={algoliaInsights.queryId} page={0} />
                            )}
                        </div>
                    </>
                )}
                {emptySuggestions && (
                    <>
                        <SuggestionsTermList terms={searchSuggestions} isEmpty />
                        <div className='h-[315px] mr-[56px] border-l border-neutral-4 hidden lg:block' />
                        {!isMobile && (
                            <div class='hidden lg:block flex-1'>
                                <SuggestionsProductList
                                    products={products ?? []}
                                    searchValue={searchValue}
                                    query={query ?? ''}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}
